import { motion } from 'framer-motion';
import {
  ChefHat, Package, Wallet, Users, Trophy, Sparkles,
  CalendarClock, ShieldCheck, BarChart3, FileText, Camera, MessageSquareText,
} from 'lucide-react';
import { SABRIL_PIXAR_AVATAR_URL } from './SabrilPixarAvatar';

/**
 * AppFeaturesGrid — iOS Home-Screen style squircle grid.
 * Each icon is clickable and smooth-scrolls to its dedicated section below.
 * "Chat IA" / "Sabril" trigger the floating Sabril Closer bubble.
 */

const scrollToTarget = (target) => {
  if (!target) return;
  // Custom intents (open bubble, etc.)
  if (target.startsWith('sabril:')) {
    try {
      window.dispatchEvent(new CustomEvent('sabril:open', { detail: { intent: target.split(':')[1] || 'project' } }));
    } catch (_) { /* noop */ }
    return;
  }
  // CSS selector
  const el = document.querySelector(target);
  if (!el) return;
  // Smooth scroll with a small offset so the card is fully visible under any sticky nav
  const top = el.getBoundingClientRect().top + window.scrollY - 90;
  window.scrollTo({ top, behavior: 'smooth' });
  // Flash highlight
  el.classList.add('app-feature-target-flash');
  setTimeout(() => el.classList.remove('app-feature-target-flash'), 1400);
};

const HUBS = [
  { icon: ChefHat,   label: 'Cuisine',      sub: '& Stocks',     target: '#hub-cuisine',      bg: 'linear-gradient(135deg, #F05A1A 0%, #FF7A3D 100%)' },
  { icon: Package,   label: 'Fournisseurs', sub: 'OCR factures', target: '#hub-fournisseurs', bg: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)' },
  { icon: Wallet,    label: 'Finance',      sub: 'EBE / marges', target: '#hub-finance',      bg: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' },
  { icon: Users,     label: 'Équipe',       sub: 'Planning · RH', target: '#hub-rh',          bg: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)' },
  { icon: Trophy,    label: 'Perf.',        sub: 'Primes · Litiges', target: '#hub-performance', bg: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)' },
  { icon: Sparkles,  label: 'Marketing',    sub: 'IA · Réseaux', target: '#hub-marketing',    bg: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)' },
  // Transversal apps inside the suite
  { icon: ShieldCheck,       label: 'HACCP',    sub: 'Compliance',  target: '#hub-cuisine',                            bg: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)' },
  { icon: CalendarClock,     label: 'Planning', sub: 'IA pondéré',  target: '[data-testid="mock-planning"]',           bg: 'linear-gradient(135deg, #0EA5E9 0%, #38BDF8 100%)' },
  { icon: BarChart3,         label: 'Pilotage', sub: 'Dashboard',   target: '[data-testid="mock-rentabilite"]',        bg: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)' },
  { icon: FileText,          label: 'Contrats', sub: 'CDI · CDD',   target: '#hub-rh',                                 bg: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)' },
  { icon: Camera,            label: 'Pointage', sub: 'Selfie + tenue', target: '[data-testid="mock-pointage"]',        bg: 'linear-gradient(135deg, #EF4444 0%, #F87171 100%)' },
  { icon: MessageSquareText, label: 'Chat IA',  sub: 'Sabril 24/7', target: 'sabril:project',                          bg: 'linear-gradient(135deg, #14B8A6 0%, #2DD4BF 100%)' },
];

const AGENTS = [
  { key: 'clara',  name: 'Clara',  role: 'Réseaux',     target: '[data-testid="mock-social"]',      img: '/agents/clara.png',  bg: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)' },
  { key: 'sophie', name: 'Sophie', role: 'RH · Planning', target: '[data-testid="mock-planning"]',  img: '/agents/sophie.png', bg: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)' },
  { key: 'leo',    name: 'Léo',    role: 'Cuisine',     target: '[data-testid="mock-recettes"]',    img: '/agents/leo.png',    bg: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' },
  { key: 'manu',   name: 'Manu',   role: 'Recrutement', target: '#hub-rh',                          img: '/agents/manu.png',   bg: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)' },
  { key: 'theo',   name: 'Théo',   role: 'Finance',     target: '[data-testid="mock-rentabilite"]', img: '/agents/theo.png',    bg: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)' },
  { key: 'sabril', name: 'Sabril', role: 'Coach IA',    target: 'sabril:project',                   img: SABRIL_PIXAR_AVATAR_URL, bg: 'linear-gradient(135deg, #F05A1A 0%, #FBBF24 100%)' },
];

const SquircleIcon = ({ icon: Icon, label, sub, bg, target, delay = 0, dataTestid }) => (
  <motion.button
    type="button"
    initial={{ opacity: 0, y: 12, scale: 0.9 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ delay, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -3, scale: 1.04 }}
    whileTap={{ scale: 0.94 }}
    onClick={() => scrollToTarget(target)}
    className="flex flex-col items-center gap-2 select-none cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F05A1A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0505] rounded-2xl"
    data-testid={dataTestid}
    aria-label={`Ouvrir la section ${label}`}
  >
    <div
      className="relative w-[64px] h-[64px] sm:w-[72px] sm:h-[72px] flex items-center justify-center"
      style={{
        background: bg,
        borderRadius: '28%',
        boxShadow: '0 8px 22px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -2px 6px rgba(0,0,0,0.18)',
      }}
    >
      <div
        className="absolute inset-x-2 top-1.5 h-2 rounded-full opacity-50 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.7) 0%, transparent 100%)' }}
      />
      <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white relative z-[1] drop-shadow" strokeWidth={2.25} />
    </div>
    <div className="text-center leading-tight">
      <p className="text-[11px] sm:text-[12px] font-black text-white tracking-tight">{label}</p>
      {sub && <p className="text-[9px] sm:text-[10px] text-white/55 font-medium">{sub}</p>}
    </div>
  </motion.button>
);

const AgentSquircle = ({ agent, delay = 0 }) => (
  <motion.button
    type="button"
    initial={{ opacity: 0, y: 12, scale: 0.9 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ delay, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -3, scale: 1.04 }}
    whileTap={{ scale: 0.94 }}
    onClick={() => scrollToTarget(agent.target)}
    className="flex flex-col items-center gap-2 select-none cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F05A1A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0505] rounded-2xl"
    data-testid={`app-agent-${agent.key}`}
    aria-label={`Voir l'agent ${agent.name}`}
  >
    <div
      className="relative w-[64px] h-[64px] sm:w-[72px] sm:h-[72px] overflow-hidden"
      style={{
        background: agent.bg,
        borderRadius: '28%',
        boxShadow: '0 8px 22px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -2px 6px rgba(0,0,0,0.18)',
      }}
    >
      <img
        src={agent.img}
        alt={agent.name}
        className="absolute inset-0 w-full h-full object-cover"
        style={agent.key === 'sabril' ? { objectPosition: '50% 18%' } : undefined}
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      <div
        className="absolute inset-x-2 top-1.5 h-2 rounded-full opacity-40 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.8) 0%, transparent 100%)' }}
      />
      <span className="absolute bottom-1 right-1 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#0a0505]" />
    </div>
    <div className="text-center leading-tight">
      <p className="text-[11px] sm:text-[12px] font-black text-white tracking-tight">{agent.name}</p>
      <p className="text-[9px] sm:text-[10px] text-white/55 font-medium">{agent.role}</p>
    </div>
  </motion.button>
);

const AppFeaturesGrid = () => {
  return (
    <div
      className="mt-10 sm:mt-12 rounded-[32px] p-5 sm:p-8 relative overflow-hidden"
      style={{
        background:
          'radial-gradient(circle at 20% 0%, rgba(240,90,26,0.18) 0%, transparent 55%), radial-gradient(circle at 90% 100%, rgba(139,92,246,0.18) 0%, transparent 55%), linear-gradient(180deg, #15090a 0%, #0a0505 100%)',
        boxShadow: '0 30px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
      }}
      data-testid="app-features-grid"
    >
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
        aria-hidden="true"
      />

      <div className="relative">
        <p className="text-[10px] sm:text-xs font-black tracking-[3px] uppercase mb-2 text-center" style={{ color: '#F05A1A' }}>
          📱 Comme ton iPhone — toute la suite en 1 app
        </p>
        <h3 className="font-bebas text-white text-center leading-[0.95] mb-2" style={{ fontSize: 'clamp(28px, 5vw, 44px)' }}>
          12 OUTILS · 6 AGENTS IA
        </h3>
        <p className="text-white/55 text-center text-xs sm:text-sm max-w-md mx-auto mb-7 sm:mb-9">
          Touche une icône — on t'amène direct au feature. Comme une vraie home iOS.
        </p>

        <div
          className="grid grid-cols-4 sm:grid-cols-6 gap-x-3 gap-y-5 sm:gap-x-5 sm:gap-y-6 mb-9 sm:mb-10"
          data-testid="app-features-hubs"
        >
          {HUBS.map((h, i) => (
            <SquircleIcon
              key={h.label}
              icon={h.icon}
              label={h.label}
              sub={h.sub}
              bg={h.bg}
              target={h.target}
              delay={i * 0.04}
              dataTestid={`app-hub-${h.label.toLowerCase().replace(/[^a-z]/g, '')}`}
            />
          ))}
        </div>

        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-white/10" />
          <p className="text-[9px] sm:text-[10px] font-black tracking-[2.5px] uppercase text-white/55">
            Ton équipe IA · 24/7
          </p>
          <div className="flex-1 h-px bg-white/10" />
        </div>
        <div
          className="grid grid-cols-3 sm:grid-cols-6 gap-x-3 gap-y-5 sm:gap-x-5 sm:gap-y-6"
          data-testid="app-features-agents"
        >
          {AGENTS.map((a, i) => (
            <AgentSquircle key={a.key} agent={a} delay={i * 0.05} />
          ))}
        </div>

        <p className="text-center text-white/45 text-[11px] sm:text-xs mt-7 sm:mt-9 italic">
          Tout est inclus dans <strong className="text-white not-italic">l'abonnement 229€/mois</strong>. Pas de modules à débloquer, pas de surprise.
        </p>
      </div>

      {/* Flash-highlight animation for scroll target */}
      <style>{`
        .app-feature-target-flash {
          animation: app-feature-flash 1.4s ease-out;
        }
        @keyframes app-feature-flash {
          0%   { box-shadow: 0 0 0 0 rgba(240,90,26,0); }
          25%  { box-shadow: 0 0 0 6px rgba(240,90,26,0.55); }
          100% { box-shadow: 0 0 0 0 rgba(240,90,26,0); }
        }
      `}</style>
    </div>
  );
};

export default AppFeaturesGrid;
